// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishListsData from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridItem from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const recentWishListsParameter: GetWishListsApiParameter = {
    page: 1,
    pageSize: 3,
    expand: ["top3products"],
    wishListLinesSort: "mostRecent",
    sort: "ModifiedOn DESC",
};

const mapStateToProps = (state: ApplicationState) => ({
    recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
    myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
    myListsDetailsPageLink: getPageLinkByPageType(state, "MyListsDetailsPage"),
    canViewWishLists: xIsAuthenticated(state),
    language: state.context.session.language,
});

const mapDispatchToProps = {
    loadWishListsData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MiniMyProjectsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    noWishListsContainer?: InjectableCss;
    noWishListsText?: TypographyPresentationProps;
    itemsContainer?: InjectableCss;
    itemContainer?: InjectableCss;
    itemImageContainer?: InjectableCss;
    itemImage?: LazyImageProps;
    itemInfoContainer?: InjectableCss;
    itemNameLink?: LinkPresentationProps;
    itemUpdatedOn?: TypographyPresentationProps;
    viewAllLink?: LinkPresentationProps;
}

export const miniMyProjectsStyles: MiniMyProjectsStyles = {};

const styles = miniMyProjectsStyles;
const MiniMyProjects: FC<Props> = ({
    myListsDetailsPageLink,
    myListsPageNavLink,
    loadWishListsData,
    canViewWishLists,
    recentWishListsDataView,
    language,
}) => {
    useEffect(() => {
        if (canViewWishLists && !recentWishListsDataView.value && !recentWishListsDataView.isLoading) {
            loadWishListsData(recentWishListsParameter);
        }
    }, [recentWishListsDataView]);

    if (!canViewWishLists) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="mini-my-projects">
            <Typography {...styles.title}>{translate("My Projects")}</Typography>
            {recentWishListsDataView.value && recentWishListsDataView.value.length === 0 && (
                <StyledWrapper {...styles.noWishListsContainer}>
                    <Typography {...styles.noWishListsText}>{siteMessage("WishLists_NoWishlistsFound")}</Typography>
                </StyledWrapper>
            )}
            {recentWishListsDataView.value && recentWishListsDataView.value.length > 0 && (
                <>
                    <StyledWrapper {...styles.itemsContainer}>
                        {recentWishListsDataView.value.map((wishList: WishListModel) => {
                            const updatedOnDisplay = getLocalizedDateTime({
                                dateTime: new Date(wishList.updatedOn),
                                language,
                                options: {
                                    year: "numeric",
                                    month: "numeric",
                                    day: "numeric",
                                },
                            });

                            const firstProduct = (wishList.wishListLineCollection?.length || 1) - 1;
                            const smallImagePath = wishList.wishListLineCollection?.[firstProduct]?.smallImagePath;
                            const altText = wishList.wishListLineCollection?.[firstProduct]?.altText || wishList.name;
                            const detailsPath = `${myListsDetailsPageLink?.url}?id=${wishList.id}`;
                            return (
                                <StyledWrapper key={wishList.id} {...styles.itemContainer}>
                                    <StyledWrapper {...styles.itemImageContainer}>
                                        <Clickable href={detailsPath}>
                                            <VisuallyHidden as="label">{altText}</VisuallyHidden>
                                            <LazyImage src={smallImagePath} altText={altText} {...styles.itemImage} />
                                        </Clickable>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.itemInfoContainer}>
                                        <Link href={detailsPath} {...styles.itemNameLink} aria-label={wishList.name}>
                                            {wishList.name}
                                        </Link>
                                        <Typography {...styles.itemUpdatedOn}>{`${translate(
                                            "Updated",
                                        )} ${updatedOnDisplay}`}</Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                    <Link
                        href={myListsPageNavLink?.url}
                        {...styles.viewAllLink}
                        aria-label={translate("View All Projects")}
                    >
                        {translate("View All Projects")}
                    </Link>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MiniMyProjects),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
    },
};

export default widgetModule;
